export function degToCompass(deg: number): string {
  const dirs = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];
  const ix = Math.round(((deg % 360) / 45)) % 8;
  return dirs[ix];
}

// Colours follow the shared platform bands (0-10 scale) so a badge's colour
// always agrees with its sport label.
export function scoreBadgeColor(score: number): {
  bg: string;
  text: string;
  label: string;
} {
  if (score >= 9)
    return { bg: "bg-emerald-600", text: "text-white", label: "Hero" };
  if (score >= 7.5)
    return { bg: "bg-emerald-500", text: "text-white", label: "Firing" };
  if (score >= 6.5)
    return { bg: "bg-lime-500", text: "text-white", label: "Great" };
  if (score >= 5)
    return { bg: "bg-amber-500", text: "text-white", label: "Good" };
  if (score >= 3.5)
    return { bg: "bg-orange-500", text: "text-white", label: "Fair" };
  if (score >= 2)
    return { bg: "bg-orange-600", text: "text-white", label: "Poor" };
  return { bg: "bg-red-500", text: "text-white", label: "Flat" };
}

export function scoringMethodLabel(sport: string): string {
  switch (sport) {
    case "surf":
    case "wind":
    case "foil":
    case "downwind-foil":
    case "wing-foil":
      return "Scored from best 3-hour window";
    case "mtb":
      return "Scored from best 4-hour window";
    case "diving":
    case "swim":
    case "swimming":
    case "sup":
      return "Blended day and session score";
    case "sailing_club":
    case "sailing_hp":
      return "Scored across the racing day";
    case "piste":
    case "off-piste":
    case "touring":
      return "Scored across the ski day";
    default:
      return "Daily score";
  }
}

// --- packing notes ---

type SkiPackingInput = {
  tempMinC: number;
  tempMaxC: number;
  snowfallCm: number;
  windSpeedKmh: number;
  weatherCode: number;
};

type SurfPackingInput = {
  swellHeightM: number;
  windSpeedKmh: number;
  waterTempC: number | null;
  weatherCode: number;
};

function generateSkiPackingNotes(days: SkiPackingInput[]): string[] {
  if (days.length === 0) return [];
  const notes: Array<{ priority: number; text: string }> = [];

  const minTemp = Math.min(...days.map((d) => d.tempMinC));
  const maxTemp = Math.max(...days.map((d) => d.tempMaxC));
  const totalSnow = days.reduce((a, d) => a + d.snowfallCm, 0);
  const maxWind = Math.max(...days.map((d) => d.windSpeedKmh));
  const clearDays = days.filter((d) => d.weatherCode <= 2).length;
  const rainDays = days.filter(
    (d) =>
      (d.weatherCode >= 51 && d.weatherCode <= 67) ||
      (d.weatherCode >= 80 && d.weatherCode <= 82)
  ).length;

  if (totalSnow >= 20) {
    notes.push({
      priority: 1,
      text: `${Math.round(totalSnow)} cm of fresh snow expected. Powder gear ready.`,
    });
  }

  if (rainDays >= 2) {
    notes.push({
      priority: 2,
      text: "Waterproof outer layers essential. Rain in the forecast.",
    });
  } else if (rainDays === 1) {
    notes.push({
      priority: 4,
      text: "Rain gear advisable for at least one day.",
    });
  }

  if (minTemp < -15) {
    notes.push({
      priority: 2,
      text: `Temperatures dropping to ${Math.round(minTemp)}°C. Heavy insulation, balaclava and hand warmers.`,
    });
  } else if (minTemp < -8) {
    notes.push({
      priority: 3,
      text: `Warm base layers and a wind-proof shell. Temperatures around ${Math.round(minTemp)}°C.`,
    });
  } else if (maxTemp > 5) {
    notes.push({
      priority: 3,
      text: "Spring conditions. Lighter layers, sun cream and sunglasses.",
    });
  } else {
    notes.push({
      priority: 5,
      text: `Pack layers for ${Math.round(minTemp)}°C to ${Math.round(maxTemp)}°C.`,
    });
  }

  if (maxWind > 60) {
    notes.push({
      priority: 2,
      text: "Gale-force wind expected. Goggles, facemask, and a back-up plan if lifts close.",
    });
  } else if (maxWind > 40) {
    notes.push({
      priority: 4,
      text: "Strong winds on some days. Wind-proof shell and secure goggles.",
    });
  }

  if (
    clearDays >= 3 &&
    maxTemp > -5 &&
    !notes.some((n) => n.text.includes("sun cream"))
  ) {
    notes.push({
      priority: 4,
      text: "Several clear days ahead. Sun cream and polarised lenses.",
    });
  }

  const fogDays = days.filter(
    (d) => d.weatherCode >= 45 && d.weatherCode <= 48
  ).length;
  if (fogDays >= 2) {
    notes.push({
      priority: 3,
      text: "Low-visibility goggles for foggy conditions.",
    });
  }

  return notes
    .sort((a, b) => a.priority - b.priority)
    .slice(0, 3)
    .map((n) => n.text);
}

function generateSurfPackingNotes(days: SurfPackingInput[]): string[] {
  if (days.length === 0) return [];
  const notes: Array<{ priority: number; text: string }> = [];

  const temps = days
    .map((d) => d.waterTempC)
    .filter((t): t is number => t !== null);
  const avgWaterTemp = temps.length > 0
    ? temps.reduce((a, b) => a + b, 0) / temps.length
    : null;

  const avgSwell =
    days.reduce((a, d) => a + d.swellHeightM, 0) / days.length;
  const maxWind = Math.max(...days.map((d) => d.windSpeedKmh));
  const clearDays = days.filter((d) => d.weatherCode <= 2).length;

  if (avgWaterTemp !== null) {
    if (avgWaterTemp < 12)
      notes.push({
        priority: 1,
        text: `Cold water (${Math.round(avgWaterTemp)}°C). 5/4mm wetsuit, booties and gloves.`,
      });
    else if (avgWaterTemp < 16)
      notes.push({
        priority: 1,
        text: `4/3mm wetsuit recommended for ${Math.round(avgWaterTemp)}°C water.`,
      });
    else if (avgWaterTemp < 20)
      notes.push({
        priority: 2,
        text: `3/2mm wetsuit or spring suit for ${Math.round(avgWaterTemp)}°C water.`,
      });
    else if (avgWaterTemp < 24)
      notes.push({
        priority: 3,
        text: `Warm ${Math.round(avgWaterTemp)}°C water. Shortie or boardshorts.`,
      });
    else
      notes.push({
        priority: 3,
        text: `Boardshorts weather. ${Math.round(avgWaterTemp)}°C water.`,
      });
  }

  if (avgSwell > 2.5) {
    notes.push({
      priority: 2,
      text: `Larger swell averaging ${avgSwell.toFixed(1)} m. Step-up board or gun.`,
    });
  } else if (avgSwell < 1.0) {
    notes.push({
      priority: 2,
      text: "Small waves expected. Longboard or fish to make the most of it.",
    });
  } else {
    notes.push({
      priority: 4,
      text: "Standard shortboard conditions.",
    });
  }

  if (maxWind > 30) {
    notes.push({
      priority: 3,
      text: "Strong winds on some days. Check the forecast each morning.",
    });
  }

  if (clearDays >= 3) {
    notes.push({
      priority: 3,
      text: "Sunny days ahead. Sun cream and a rashie.",
    });
  }

  const rainDays = days.filter(
    (d) =>
      (d.weatherCode >= 51 && d.weatherCode <= 67) ||
      (d.weatherCode >= 80 && d.weatherCode <= 82)
  ).length;
  if (rainDays >= 2) {
    notes.push({
      priority: 4,
      text: "Rain jacket for between sessions.",
    });
  }

  return notes
    .sort((a, b) => a.priority - b.priority)
    .slice(0, 3)
    .map((n) => n.text);
}

// --- off-piste ---

function generateOffPistePackingNotes(days: SkiPackingInput[]): string[] {
  if (days.length === 0) return [];
  const notes: Array<{ priority: number; text: string }> = [];

  const minTemp = Math.min(...days.map((d) => d.tempMinC));
  const maxTemp = Math.max(...days.map((d) => d.tempMaxC));
  const totalSnow = days.reduce((a, d) => a + d.snowfallCm, 0);
  const maxWind = Math.max(...days.map((d) => d.windSpeedKmh));
  const clearDays = days.filter((d) => d.weatherCode <= 2).length;
  const fogDays = days.filter((d) => d.weatherCode >= 45 && d.weatherCode <= 48).length;

  notes.push({
    priority: 1,
    text: "Avalanche beacon, shovel and probe are essential off-piste.",
  });

  if (totalSnow >= 20) {
    notes.push({
      priority: 1,
      text: `${Math.round(totalSnow)} cm of fresh snow expected. Fat skis or a powder board.`,
    });
  } else if (totalSnow >= 5) {
    notes.push({
      priority: 3,
      text: "Some fresh snow expected. All-mountain setup should work well.",
    });
  }

  if (minTemp < -15) {
    notes.push({
      priority: 2,
      text: `Temperatures dropping to ${Math.round(minTemp)}°C. Heavy insulation and hand warmers.`,
    });
  } else if (minTemp < -8) {
    notes.push({
      priority: 3,
      text: `Warm base layers and wind-proof shell for ${Math.round(minTemp)}°C.`,
    });
  } else if (maxTemp > 5) {
    notes.push({
      priority: 3,
      text: "Warm temps increase wet-slide risk. Lighter layers but stay alert.",
    });
  }

  if (maxWind > 50) {
    notes.push({
      priority: 2,
      text: "High winds forecast. Wind slab risk elevated on lee slopes.",
    });
  }

  if (fogDays >= 2) {
    notes.push({
      priority: 2,
      text: "Low visibility expected. Stick to familiar terrain or hire a guide.",
    });
  } else if (clearDays >= 3 && maxTemp > -5) {
    notes.push({
      priority: 4,
      text: "Clear days ahead. Sun cream and high-contrast lenses.",
    });
  }

  return notes
    .sort((a, b) => a.priority - b.priority)
    .slice(0, 3)
    .map((n) => n.text);
}

// --- touring ---

function generateTouringPackingNotes(days: SkiPackingInput[]): string[] {
  if (days.length === 0) return [];
  const notes: Array<{ priority: number; text: string }> = [];

  const minTemp = Math.min(...days.map((d) => d.tempMinC));
  const maxTemp = Math.max(...days.map((d) => d.tempMaxC));
  const totalSnow = days.reduce((a, d) => a + d.snowfallCm, 0);
  const maxWind = Math.max(...days.map((d) => d.windSpeedKmh));
  const clearDays = days.filter((d) => d.weatherCode <= 2).length;
  const rainDays = days.filter(
    (d) =>
      (d.weatherCode >= 51 && d.weatherCode <= 67) ||
      (d.weatherCode >= 80 && d.weatherCode <= 82)
  ).length;

  notes.push({
    priority: 1,
    text: "Avalanche beacon, shovel, probe and skins. Check the bulletin before heading out.",
  });

  if (minTemp < -12) {
    notes.push({
      priority: 2,
      text: `Cold conditions (${Math.round(minTemp)}°C). Layering is key: breathable base for the climb, insulated shell for the descent.`,
    });
  } else if (maxTemp > 5) {
    notes.push({
      priority: 2,
      text: "Warm temps. Lightweight breathable layers, sun cream and plenty of water.",
    });
  } else {
    notes.push({
      priority: 3,
      text: `Dress in layers for ${Math.round(minTemp)}°C to ${Math.round(maxTemp)}°C. Breathable base for uphills, warm shell for descents.`,
    });
  }

  if (totalSnow >= 20) {
    notes.push({
      priority: 2,
      text: `${Math.round(totalSnow)} cm of fresh snow. Powder baskets and wider skis advised. Assess avalanche risk carefully.`,
    });
  }

  if (maxWind > 50) {
    notes.push({
      priority: 2,
      text: "Strong ridge winds expected. Goggles, balaclava and consider lower routes.",
    });
  } else if (maxWind > 35) {
    notes.push({
      priority: 3,
      text: "Moderate wind on exposed ridges. Wind-proof shell and secure goggles.",
    });
  }

  if (rainDays >= 2) {
    notes.push({
      priority: 3,
      text: "Rain in the forecast. Waterproof shell and dry bags for spare layers.",
    });
  }

  if (clearDays >= 3 && !notes.some((n) => n.text.includes("sun cream"))) {
    notes.push({
      priority: 4,
      text: "Clear days ahead. Sun cream, lip balm and glacier glasses on exposed terrain.",
    });
  }

  return notes
    .sort((a, b) => a.priority - b.priority)
    .slice(0, 3)
    .map((n) => n.text);
}

// --- MTB ---

function generateMtbPackingNotes(days: SkiPackingInput[]): string[] {
  if (days.length === 0) return [];
  const notes: Array<{ priority: number; text: string }> = [];

  const minTemp = Math.min(...days.map((d) => d.tempMinC));
  const maxTemp = Math.max(...days.map((d) => d.tempMaxC));
  const maxWind = Math.max(...days.map((d) => d.windSpeedKmh));
  const clearDays = days.filter((d) => d.weatherCode <= 2).length;
  const rainDays = days.filter(
    (d) =>
      (d.weatherCode >= 51 && d.weatherCode <= 67) ||
      (d.weatherCode >= 80 && d.weatherCode <= 82)
  ).length;

  if (rainDays >= 2) {
    notes.push({
      priority: 1,
      text: "Muddy trails likely. Waterproof jacket, mud tyres and mudguards.",
    });
  } else if (rainDays === 1) {
    notes.push({
      priority: 3,
      text: "Rain on at least one day. Pack a lightweight waterproof.",
    });
  }

  if (maxTemp > 25) {
    notes.push({
      priority: 2,
      text: `Hot riding (${Math.round(maxTemp)}°C). Plenty of water, sun cream and a ventilated helmet.`,
    });
  } else if (maxTemp > 15) {
    notes.push({
      priority: 3,
      text: `Comfortable temperatures (${Math.round(minTemp)}°C to ${Math.round(maxTemp)}°C). Jersey and arm warmers.`,
    });
  } else if (minTemp < 5) {
    notes.push({
      priority: 2,
      text: `Cold riding (${Math.round(minTemp)}°C). Thermal base layer, full-finger gloves and a wind-proof jacket.`,
    });
  } else {
    notes.push({
      priority: 3,
      text: `Pack layers for ${Math.round(minTemp)}°C to ${Math.round(maxTemp)}°C. Light jacket for descents.`,
    });
  }

  if (maxWind > 50) {
    notes.push({
      priority: 2,
      text: "Strong gusts forecast. Avoid exposed ridgeline trails.",
    });
  }

  if (clearDays >= 3 && maxTemp > 10 && !notes.some((n) => n.text.includes("sun cream"))) {
    notes.push({
      priority: 4,
      text: "Clear skies ahead. Sun cream, sunglasses and a hydration pack.",
    });
  }

  notes.push({
    priority: 5,
    text: "Helmet, knee pads and a basic trail repair kit.",
  });

  return notes
    .sort((a, b) => a.priority - b.priority)
    .slice(0, 3)
    .map((n) => n.text);
}

// --- wind sports (kite / windsurf) ---

function generateWindPackingNotes(days: SurfPackingInput[]): string[] {
  if (days.length === 0) return [];
  const notes: Array<{ priority: number; text: string }> = [];

  const temps = days.map((d) => d.waterTempC).filter((t): t is number => t !== null);
  const avgWaterTemp = temps.length > 0 ? temps.reduce((a, b) => a + b, 0) / temps.length : null;
  const avgWind = days.reduce((a, d) => a + d.windSpeedKmh, 0) / days.length;
  const maxWind = Math.max(...days.map((d) => d.windSpeedKmh));
  const clearDays = days.filter((d) => d.weatherCode <= 2).length;

  if (avgWaterTemp !== null) {
    if (avgWaterTemp < 12)
      notes.push({ priority: 1, text: `Cold water (${Math.round(avgWaterTemp)}°C). 5/4mm wetsuit, booties and gloves.` });
    else if (avgWaterTemp < 16)
      notes.push({ priority: 1, text: `4/3mm wetsuit for ${Math.round(avgWaterTemp)}°C water.` });
    else if (avgWaterTemp < 20)
      notes.push({ priority: 2, text: `3/2mm wetsuit or shorty for ${Math.round(avgWaterTemp)}°C water.` });
    else if (avgWaterTemp < 24)
      notes.push({ priority: 3, text: `Warm water (${Math.round(avgWaterTemp)}°C). Shorty or boardshorts with a harness top.` });
    else
      notes.push({ priority: 3, text: `Boardshorts weather (${Math.round(avgWaterTemp)}°C). Harness rash vest recommended.` });
  }

  if (avgWind > 35) {
    notes.push({ priority: 1, text: `Strong winds averaging ${Math.round(avgWind)} km/h. Smaller kite or sail. Check conditions before launching.` });
  } else if (avgWind > 20) {
    notes.push({ priority: 2, text: `Good wind averaging ${Math.round(avgWind)} km/h. Mid-range kite or sail should cover it.` });
  } else {
    notes.push({ priority: 3, text: `Light winds averaging ${Math.round(avgWind)} km/h. Bring your largest kite or sail.` });
  }

  if (maxWind > 50) {
    notes.push({ priority: 2, text: "Gusts above 50 km/h on some days. Safety leash and impact vest advised." });
  }

  if (clearDays >= 3) {
    notes.push({ priority: 4, text: "Sunny days ahead. Sun cream and UV-rated sunglasses." });
  }

  return notes
    .sort((a, b) => a.priority - b.priority)
    .slice(0, 3)
    .map((n) => n.text);
}

// --- foil ---

function generateFoilPackingNotes(days: SurfPackingInput[]): string[] {
  if (days.length === 0) return [];
  const notes: Array<{ priority: number; text: string }> = [];

  const temps = days.map((d) => d.waterTempC).filter((t): t is number => t !== null);
  const avgWaterTemp = temps.length > 0 ? temps.reduce((a, b) => a + b, 0) / temps.length : null;
  const avgWind = days.reduce((a, d) => a + d.windSpeedKmh, 0) / days.length;
  const avgSwell = days.reduce((a, d) => a + d.swellHeightM, 0) / days.length;
  const clearDays = days.filter((d) => d.weatherCode <= 2).length;

  notes.push({ priority: 1, text: "Impact vest and helmet recommended when foiling." });

  if (avgWaterTemp !== null) {
    if (avgWaterTemp < 14)
      notes.push({ priority: 1, text: `Cold water (${Math.round(avgWaterTemp)}°C). 4/3mm wetsuit, booties and gloves.` });
    else if (avgWaterTemp < 20)
      notes.push({ priority: 2, text: `${Math.round(avgWaterTemp)}°C water. 3/2mm wetsuit or spring suit.` });
    else
      notes.push({ priority: 3, text: `Warm water (${Math.round(avgWaterTemp)}°C). Boardshorts and a rashie.` });
  }

  if (avgWind < 12 && avgSwell < 0.8) {
    notes.push({ priority: 2, text: "Light conditions. Larger foil wing for extra lift." });
  } else if (avgWind > 30 || avgSwell > 2.0) {
    notes.push({ priority: 2, text: "Powerful conditions. Smaller foil wing and a safety leash." });
  }

  if (clearDays >= 3) {
    notes.push({ priority: 4, text: "Clear skies ahead. Sun cream and polarised lenses." });
  }

  return notes
    .sort((a, b) => a.priority - b.priority)
    .slice(0, 3)
    .map((n) => n.text);
}

// --- diving ---

function generateDivingPackingNotes(days: SurfPackingInput[]): string[] {
  if (days.length === 0) return [];
  const notes: Array<{ priority: number; text: string }> = [];

  const temps = days.map((d) => d.waterTempC).filter((t): t is number => t !== null);
  const avgWaterTemp = temps.length > 0 ? temps.reduce((a, b) => a + b, 0) / temps.length : null;
  const avgSwell = days.reduce((a, d) => a + d.swellHeightM, 0) / days.length;
  const maxWind = Math.max(...days.map((d) => d.windSpeedKmh));
  const clearDays = days.filter((d) => d.weatherCode <= 2).length;
  const rainDays = days.filter(
    (d) => (d.weatherCode >= 51 && d.weatherCode <= 67) || (d.weatherCode >= 80 && d.weatherCode <= 82)
  ).length;

  if (avgWaterTemp !== null) {
    if (avgWaterTemp < 12)
      notes.push({ priority: 1, text: `Cold water (${Math.round(avgWaterTemp)}°C). 7mm semi-dry or drysuit, hood, gloves and booties.` });
    else if (avgWaterTemp < 16)
      notes.push({ priority: 1, text: `Cool water (${Math.round(avgWaterTemp)}°C). 5mm wetsuit with hood and gloves.` });
    else if (avgWaterTemp < 22)
      notes.push({ priority: 2, text: `${Math.round(avgWaterTemp)}°C water. 3mm wetsuit should be comfortable.` });
    else
      notes.push({ priority: 3, text: `Warm water (${Math.round(avgWaterTemp)}°C). 1mm shorty or rashie with shorts.` });
  }

  if (rainDays >= 2 || avgSwell > 1.5) {
    notes.push({ priority: 2, text: "Reduced visibility likely. Dive torch and DSMB essential." });
  } else if (clearDays >= 3 && avgSwell < 1.0) {
    notes.push({ priority: 3, text: "Good visibility expected. Great conditions for photography." });
  }

  if (maxWind > 35 || avgSwell > 2.0) {
    notes.push({ priority: 2, text: "Rough surface conditions on some days. Check boat schedules and have a backup plan." });
  }

  if (clearDays >= 3) {
    notes.push({ priority: 4, text: "Sunny days ahead. Sun cream for surface intervals." });
  }

  return notes
    .sort((a, b) => a.priority - b.priority)
    .slice(0, 3)
    .map((n) => n.text);
}

// --- SUP ---

function generateSupPackingNotes(days: SurfPackingInput[]): string[] {
  if (days.length === 0) return [];
  const notes: Array<{ priority: number; text: string }> = [];

  const temps = days.map((d) => d.waterTempC).filter((t): t is number => t !== null);
  const avgWaterTemp = temps.length > 0 ? temps.reduce((a, b) => a + b, 0) / temps.length : null;
  const maxWind = Math.max(...days.map((d) => d.windSpeedKmh));
  const avgSwell = days.reduce((a, d) => a + d.swellHeightM, 0) / days.length;
  const clearDays = days.filter((d) => d.weatherCode <= 2).length;

  notes.push({ priority: 1, text: "Coiled leash and PFD. Check wind direction before paddling offshore." });

  if (avgWaterTemp !== null) {
    if (avgWaterTemp < 14)
      notes.push({ priority: 1, text: `Cold water (${Math.round(avgWaterTemp)}°C). 4/3mm wetsuit, booties and gloves.` });
    else if (avgWaterTemp < 20)
      notes.push({ priority: 2, text: `${Math.round(avgWaterTemp)}°C water. 3/2mm wetsuit or long-sleeve rashie.` });
    else
      notes.push({ priority: 3, text: `Warm water (${Math.round(avgWaterTemp)}°C). Boardshorts and a UV rashie.` });
  }

  if (maxWind > 30) {
    notes.push({ priority: 2, text: "Strong winds on some days. Stay close to shore and paddle into the wind on the way out." });
  } else if (avgSwell > 1.5) {
    notes.push({ priority: 3, text: "Swell running. Consider a shorter, wider board for stability." });
  }

  if (clearDays >= 3) {
    notes.push({ priority: 4, text: "Sunny days ahead. Sun cream, hat and a hydration pack for longer paddles." });
  }

  return notes
    .sort((a, b) => a.priority - b.priority)
    .slice(0, 3)
    .map((n) => n.text);
}

// --- open water swimming ---

function generateSwimPackingNotes(days: SurfPackingInput[]): string[] {
  if (days.length === 0) return [];
  const notes: Array<{ priority: number; text: string }> = [];

  const temps = days.map((d) => d.waterTempC).filter((t): t is number => t !== null);
  const avgWaterTemp = temps.length > 0 ? temps.reduce((a, b) => a + b, 0) / temps.length : null;
  const maxWind = Math.max(...days.map((d) => d.windSpeedKmh));
  const avgSwell = days.reduce((a, d) => a + d.swellHeightM, 0) / days.length;
  const clearDays = days.filter((d) => d.weatherCode <= 2).length;

  if (avgWaterTemp !== null) {
    if (avgWaterTemp < 12)
      notes.push({ priority: 1, text: `Cold water (${Math.round(avgWaterTemp)}°C). Full wetsuit, neoprene cap, gloves and booties. Limit time and warm up quickly after.` });
    else if (avgWaterTemp < 16)
      notes.push({ priority: 1, text: `Cool water (${Math.round(avgWaterTemp)}°C). Full wetsuit recommended. Neoprene cap helps.` });
    else if (avgWaterTemp < 20)
      notes.push({ priority: 2, text: `${Math.round(avgWaterTemp)}°C water. Wetsuit or thermal swimsuit depending on duration.` });
    else
      notes.push({ priority: 3, text: `Comfortable ${Math.round(avgWaterTemp)}°C water. Swimsuit for shorter swims, thin wetsuit for distance.` });
  }

  notes.push({ priority: 2, text: "Brightly coloured tow float for visibility. Goggles essential." });

  if (maxWind > 25 || avgSwell > 1.5) {
    notes.push({ priority: 2, text: "Choppy conditions likely. Swim parallel to shore and have a spotter." });
  }

  if (clearDays >= 3) {
    notes.push({ priority: 4, text: "Sunny days ahead. Waterproof sun cream and tinted goggles." });
  }

  return notes
    .sort((a, b) => a.priority - b.priority)
    .slice(0, 3)
    .map((n) => n.text);
}

// --- bodyboarding ---

function generateBodyboardPackingNotes(days: SurfPackingInput[]): string[] {
  if (days.length === 0) return [];
  const notes: Array<{ priority: number; text: string }> = [];

  const temps = days.map((d) => d.waterTempC).filter((t): t is number => t !== null);
  const avgWaterTemp = temps.length > 0 ? temps.reduce((a, b) => a + b, 0) / temps.length : null;
  const avgSwell = days.reduce((a, d) => a + d.swellHeightM, 0) / days.length;
  const maxWind = Math.max(...days.map((d) => d.windSpeedKmh));

  if (avgWaterTemp !== null) {
    if (avgWaterTemp < 12)
      notes.push({ priority: 1, text: `Cold water (${Math.round(avgWaterTemp)}°C). 5/4mm wetsuit, booties and gloves.` });
    else if (avgWaterTemp < 16)
      notes.push({ priority: 1, text: `4/3mm wetsuit for ${Math.round(avgWaterTemp)}°C water.` });
    else if (avgWaterTemp < 20)
      notes.push({ priority: 2, text: `3/2mm wetsuit or spring suit for ${Math.round(avgWaterTemp)}°C water.` });
    else
      notes.push({ priority: 3, text: `Warm ${Math.round(avgWaterTemp)}°C water. Shortie or boardshorts.` });
  }

  // Bodyboard-specific kit (not surf): fins to kick into steep waves, and a
  // bicep leash rather than an ankle leash.
  notes.push({ priority: 1, text: "Swim fins with fin socks and tethers to kick into the steep takeoffs." });
  notes.push({ priority: 2, text: "Bicep leash to keep the board close in heavy shorebreak." });

  if (avgSwell < 1.0) {
    notes.push({ priority: 3, text: "Small, punchy shorebreak likely. A stiffer board holds the steep faces." });
  } else if (avgSwell > 2.0) {
    notes.push({ priority: 2, text: `Powerful surf averaging ${avgSwell.toFixed(1)} m. A larger, stiffer board for drop-knee control.` });
  }

  if (maxWind > 30) {
    notes.push({ priority: 3, text: "Strong winds on some days. Aim for the morning glass-off window." });
  }

  return notes
    .sort((a, b) => a.priority - b.priority)
    .slice(0, 4)
    .map((n) => n.text);
}

// --- dispatch ---

export type PackingSport =
  | "ski" | "piste" | "off-piste" | "touring" | "mtb"
  | "surf" | "wind" | "foil" | "downwind-foil" | "wing-foil"
  | "diving" | "sup" | "swim" | "bodyboarding";

export function generatePackingNotes(
  forecast: SkiPackingInput[] | SurfPackingInput[],
  sport: PackingSport
): string[] {
  switch (sport) {
    case "ski":
    case "piste":
      return generateSkiPackingNotes(forecast as SkiPackingInput[]);
    case "off-piste":
      return generateOffPistePackingNotes(forecast as SkiPackingInput[]);
    case "touring":
      return generateTouringPackingNotes(forecast as SkiPackingInput[]);
    case "mtb":
      return generateMtbPackingNotes(forecast as SkiPackingInput[]);
    case "surf":
      return generateSurfPackingNotes(forecast as SurfPackingInput[]);
    case "wind":
      return generateWindPackingNotes(forecast as SurfPackingInput[]);
    case "foil":
    case "downwind-foil":
      return generateFoilPackingNotes(forecast as SurfPackingInput[]);
    case "wing-foil":
      return generateWindPackingNotes(forecast as SurfPackingInput[]);
    case "diving":
      return generateDivingPackingNotes(forecast as SurfPackingInput[]);
    case "sup":
      return generateSupPackingNotes(forecast as SurfPackingInput[]);
    case "swim":
      return generateSwimPackingNotes(forecast as SurfPackingInput[]);
    case "bodyboarding":
      return generateBodyboardPackingNotes(forecast as SurfPackingInput[]);
    default:
      return generateSurfPackingNotes(forecast as SurfPackingInput[]);
  }
}
